import { SupportedLanguages } from '@core/_globals/types/sharedTypes';
import ISigner from '@core/_interfaces/ISigner';
import CoverageTerm from '@core/classes/CoverageTerm';
import FIMenuCustomer from '@core/classes/FIMenuCustomer';
import FIMenuCustomerViewed from '@core/classes/FIMenuCustomerViewed';
import FIMenuLeaseTerms from '@core/classes/FIMenuLeaseTerms';
import FIMenuLender from '@core/classes/FIMenuLender';
import FIMenuLoanTerms from '@core/classes/FIMenuLoanTerms';
import FIMenuVehicle from '@core/classes/FIMenuVehicle';
import PaperworkPacket from '@core/classes/Paperwork/PaperworkPacket';
import VehicleWarranty from '@core/classes/VehicleWarranty';

export default class FIMenuForCustomerMeeting {
	language: SupportedLanguages = 'English'; // Default language is English
	storeInfo: any = {}; // storeInfo is used in customer application
	storeCode: string = null;
	storeState: string = null;
	coverageTerms: CoverageTerm[] = [];
	customerViewed: FIMenuCustomerViewed[] = [];
	customer: CustomerLite = null;
	coCustomer: CustomerLite = null;
	customersInfo: any = null;
	hasCoSigner: boolean = null;
	googleReviewsURL: string = '';

	dealType: string = null;
	dealDate: string = null;
	otd: number = null;

	lender: ILenderLite = null;
	leaseTerms: FIMenuLeaseTerms = new FIMenuLeaseTerms();
	loanTerms: FIMenuLoanTerms = new FIMenuLoanTerms();
	isDraft: boolean = null;
	pricesReady: boolean = null;
	calculatedFinalNumbers: ICalculatedFinalNumbers = null;
	disclaimerPayment: number = null;

	vin: string = null;
	inventoryType: string = null;
	vehicle: FIMenuVehicle = null;
	vehicleMiles: number = null;
	vehicleWarranty = new VehicleWarranty();
	currentPacket: any = null;
	fimanagerSignature: string = null;

	constructor(init?: any) {
		if (init) {
			this.language = init.language ?? 'English';
			this.storeInfo = init.storeInfo ?? {};
			this.storeCode = init.storeCode ?? null;
			this.storeState = init.storeState ?? null;
			this.coverageTerms = init.coverageTerms?.map((ct: CoverageTerm) => new CoverageTerm(ct)) ?? [];
			this.customerViewed = init.customerViewed ? [...init.customerViewed] : [];
			this.customer = init.customer ? this.getFilteredCustomerData(init.customer) : null;
			this.coCustomer = init.coCustomer ? this.getFilteredCustomerData(init.coCustomer) : null;
			this.customersInfo = init.customersInfo ? this.setCustomersInfo(init.customersInfo) : null;
			this.hasCoSigner = init.hasCoSigner ?? null;
			this.googleReviewsURL = init.googleReviewsURL ?? '';

			this.dealType = init.dealType ?? null;
			this.dealDate = init.dealDate ?? null;
			this.otd = init.otd ?? null;

			this.lender = init.lender ? this.getFilteredLenderData(init.lender) : null;
			this.leaseTerms = init.leaseTerms ? new FIMenuLeaseTerms(init.leaseTerms) : new FIMenuLeaseTerms();
			this.loanTerms = init.loanTerms ? new FIMenuLoanTerms(init.loanTerms) : new FIMenuLoanTerms();
			this.isDraft = init.isDraft ?? null;
			this.pricesReady = init.pricesReady ?? null;
			this.calculatedFinalNumbers = init.calculatedFinalNumbers ?? ({} as ICalculatedFinalNumbers);
			this.disclaimerPayment = init.disclaimerPayment ?? null;

			this.vin = init.vin ?? null;
			this.inventoryType = init.inventoryType ?? null;
			this.vehicle = init.vehicle ? new FIMenuVehicle(init.vehicle) : null;
			this.vehicleMiles = init.vehicleMiles ?? null;
			this.vehicleWarranty = init.vehicleWarranty ? new VehicleWarranty(init.vehicleWarranty) : new VehicleWarranty();

			this.currentPacket = init.currentPacket ? new PaperworkPacket(init.currentPacket) : null;
		}
	}

	getFilteredCustomerData(customer: FIMenuCustomer | CustomerLite | any): CustomerLite {
		return new CustomerLite({
			id: customer.id,
			isEntity: customer.isEntity ?? false,
			// If customer is a FIMenuCustomer, we need to get the full name from the personalInfo. Else we get it from the fullName property
			fullName: customer instanceof FIMenuCustomer ? customer.getFullName() : customer.fullName,
			prefix: customer instanceof FIMenuCustomer ? customer.personalInfo?.prefix : customer.prefix,
			suffix: customer instanceof FIMenuCustomer ? customer.personalInfo?.suffix : customer.suffix,
			currentSignatureId: customer.currentSignatureId,
			currentInitialsId: customer.currentInitialsId,
			entitySignerSignatureId: customer.entitySignerSignatureId,
			entitySignerInitialsId: customer.entitySignerInitialsId,
		});
	}

	setCustomersInfo(customersInfo: any) {
		return {
			...customersInfo,
			customers: customersInfo.customers?.map((customer: any) => this.getFilteredCustomerData(customer)),
		};
	}

	getFilteredLenderData(fimenuLender: FIMenuLender): ILenderLite {
		return {
			lenderName: fimenuLender.lenderName,
			lenderCode: fimenuLender.lenderCode,
		};
	}

	selectPreferredCoverageTerm(coverageTerm?: number) {
		if (coverageTerm >= 0) {
			this.coverageTerms.forEach(term => {
				if (term.term == coverageTerm) term['preferred'] = true;
				else term['preferred'] = false;
			});
		}
	}
}
export class CustomerLite implements ISigner {
	id: string;
	isEntity: boolean;
	fullName: string;
	prefix: string;
	suffix: string;
	currentSignatureId: string | null;
	currentInitialsId: string | null;
	entitySignerSignatureId: string | null;
	entitySignerInitialsId: string | null;

	constructor(init?: Partial<CustomerLite>) {
		if (init) {
			this.id = init?.id ?? null;
			this.isEntity = init?.isEntity ?? false;
			this.fullName = init?.fullName ?? null;
			this.prefix = init?.prefix ?? null;
			this.suffix = init?.suffix ?? null;
			this.currentSignatureId = init?.currentSignatureId ?? null;
			this.currentInitialsId = init?.currentInitialsId ?? null;
			this.entitySignerSignatureId = init?.entitySignerSignatureId ?? null;
			this.entitySignerInitialsId = init?.entitySignerInitialsId ?? null;
		}
	}

	getFullName(): string {
		return this.fullName;
	}
}

export interface ILenderLite {
	lenderName: string;
	lenderCode: string;
}

export interface ICalculatedFinalNumbersFlexBuy {
	flexBuyPhase1Payment?: number;
	flexBuyPhase2Payment?: number;
	flexBuySplitMonth?: number;
}

export interface ICalculatedFinalNumbersEntry extends ICalculatedFinalNumbersFlexBuy {
	dealType?: string;
	baseAmount?: number;
	basePayment?: number;
	finalAmount?: number;
	finalPayment?: number;
	finalPaymentAtBuyRate?: number;
}

export interface ICalculatedFinalNumbersTerm {
	term: number;
	numbers: ICalculatedFinalNumbersEntry;
}

export interface ICalculatedFinalNumbersCoverage {
	termNumbers: ICalculatedFinalNumbersTerm[];
}

export interface ICalculatedFinalNumbers {
	termNumbers: ICalculatedFinalNumbersTerm[] | null;
	[coverageTerm: number]: ICalculatedFinalNumbersCoverage;
}
